import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PromotionalBanner } from '@amzn/seller-university-ui-components';
import { useBundle } from '@amzn/react-arb-tools';
import { t } from 'src/utils/translation';
import { getAppRoute } from 'src/utils/routing';
import { ROUTES } from 'src/constants/route';
import { BUNDLE_NAME, HOMEPAGE_STRING_ID } from 'src/constants/localization';
import useUserMetrics from 'src/metrics/useUserMetrics';
import { FeatureName } from 'src/metrics/metricsFields';

const BANNER_THUMBNAIL_SRC = 'https://d1dspppqvg0rnv.cloudfront.net/assets/5d02a662-0431-4b9e-8aae-9cd1e83bf8ec/original.png';

export interface HomepageCredentialBannerProps {
  /** Credential learning pathway id the banner links to (pilot: amazon-pricing-credential). */
  credentialId: string;
}

/**
 * Hero banner ingress at the top of the homepage promoting the credential experience.
 * The "Get started" CTA navigates to the credential page. The homepage gates rendering on
 * the credentials pilot weblab.
 */
const HomepageCredentialBanner: React.FC<HomepageCredentialBannerProps> = ({ credentialId }) => {
  const navigate = useNavigate();
  const [homepageBundle] = useBundle(BUNDLE_NAME.HOMEPAGE);
  const { clickMetric } = useUserMetrics();

  const handleGetStarted = () => {
    clickMetric({
      feature: FeatureName.CredentialBannerIngress,
      metadata: { url: window.location.href, credentialId },
    });
    navigate(getAppRoute(`${ROUTES.NEW_LEARN.CREDENTIAL}/${credentialId}`));
  };

  return (
    <PromotionalBanner
      heading={t(homepageBundle, HOMEPAGE_STRING_ID.CREDENTIAL_BANNER_HEADING)}
      body={t(homepageBundle, HOMEPAGE_STRING_ID.CREDENTIAL_BANNER_BODY)}
      thumbnailSrc={BANNER_THUMBNAIL_SRC}
      thumbnailAlt=""
      cta={{ label: t(homepageBundle, HOMEPAGE_STRING_ID.CREDENTIAL_BANNER_CTA), onClick: handleGetStarted }}
      badge={t(homepageBundle, HOMEPAGE_STRING_ID.CREDENTIAL_BANNER_BADGE)}
      showCloseButton={false}
      ariaLabel={t(homepageBundle, HOMEPAGE_STRING_ID.CREDENTIAL_BANNER_HEADING)}
    />
  );
};

export default HomepageCredentialBanner;
